// src/components/ColourCombinations.tsx

import React from 'react';
import { ColourCombination } from '../hooks/useColours';
import { SelectedVase, OrderItem, ColourFinish } from '../types';
import { vases } from '../data/vases';

interface ColourCombinationsProps {
  combinations: ColourCombination[];
  loading: boolean;
  error: string | null;
  selectedProduct?: SelectedVase | null;
  onAddToOrder?: (item: OrderItem) => void; // Made optional
  selectedComboId?: string | null;
  onSelectCombo?: (combo: ColourCombination) => void;
  variant?: 'default' | 'curated';
  showHeading?: boolean;
  showAddToOrderButton?: boolean;
  addToOrderDisabled?: boolean;
  addToOrderLabel?: string;
  listMaxHeightClassName?: string;
  finishLabelByValue?: Record<string, string>;
}

const ColourCombinations: React.FC<ColourCombinationsProps> = ({
  combinations,
  loading,
  error,
  selectedProduct = null,
  onAddToOrder = () => {}, // Default no-op function
  selectedComboId,
  onSelectCombo,
  variant = 'default',
  showHeading = true,
  showAddToOrderButton = true,
  addToOrderDisabled = false,
  addToOrderLabel,
  listMaxHeightClassName,
  finishLabelByValue = {}
}) => {
  const isCuratedVariant = variant === 'curated';
  const loadingMessageClass = isCuratedVariant ? 'text-sm text-stone-600' : 'mt-8 text-stone-600';
  const emptyMessageClass = isCuratedVariant ? 'text-sm text-stone-600' : 'mt-8 text-stone-600';
  const errorMessageClass = isCuratedVariant ? 'text-sm text-red-600' : 'mt-8 text-red-500';

  if (loading) {
    return <p className={loadingMessageClass}>Loading combinations...</p>;
  }

  if (error) {
    return <p className={errorMessageClass}>Error: {error}</p>;
  }

  if (combinations.length === 0) {
    return <p className={emptyMessageClass}>No combinations match your selection.</p>;
  }

  const selectedCombo = selectedComboId
    ? combinations.find(combo => combo.id === selectedComboId) ?? null
    : null;
  const handleComboSelect = (combo: ColourCombination) => {
    onSelectCombo?.(combo);
  };

  const getFinishLabel = (finishType: string) => finishLabelByValue[finishType] || finishType.replace(/_/g, ' ');
  const resolvedAddToOrderLabel =
    addToOrderLabel ||
    (selectedProduct && selectedCombo
      ? `Add "${selectedProduct.name} in ${selectedCombo.color_description || 'Custom Combination'}" to Order Request`
      : 'Add to Order Request');
  const listClasses = isCuratedVariant
    ? `space-y-2.5 ${listMaxHeightClassName ?? 'max-h-[24rem] overflow-y-auto pr-1'}`
    : 'grid grid-cols-1 gap-4';

  return (
    <div className={isCuratedVariant ? '' : 'mt-8'}>
      {showHeading && (
        <h3 className="text-xl font-medium mb-4 text-stone-700">Available Combinations</h3>
      )}
      <div className={listClasses}>
        {combinations.map((combo) => (
          <div
            key={combo.id}
            className={
              isCuratedVariant
                ? `group rounded-xl border px-3.5 py-3.5 transition-all cursor-pointer ${
                    selectedCombo?.id === combo.id
                      ? 'border-emerald-300 bg-emerald-50/60 ring-1 ring-emerald-200/90'
                      : 'border-stone-200/60 bg-white/55 hover:bg-white hover:border-stone-300/70'
                  }`
                : `bg-white p-4 rounded-md shadow-sm border-2 transition-all cursor-pointer ${
                    selectedCombo?.id === combo.id
                      ? 'border-stone-800'
                      : 'border-stone-200 hover:border-stone-300'
                  }`
            }
            onClick={() => handleComboSelect(combo)}
          >
            {isCuratedVariant ? (
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="truncate text-sm font-semibold text-stone-900">
                      {combo.color_description || 'Custom Combination'}
                    </h4>
                    {selectedCombo?.id === combo.id && (
                      <span className="ml-auto text-[11px] font-medium text-emerald-700">
                        ✓ Selected
                      </span>
                    )}
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-stone-600">
                    {getFinishLabel(combo.finish_type)}
                  </p>
                  {Array.isArray(combo.colours) && combo.colours.length > 0 && (
                    <div className="mt-2 flex items-center gap-1">
                      {combo.colours.slice(0, 5).map((colour, idx) => (
                        <span
                          key={`${combo.id}-chip-${idx}`}
                          className="h-3.5 w-3.5 rounded-full ring-1 ring-black/10"
                          style={{ backgroundColor: colour }}
                          aria-hidden="true"
                        />
                      ))}
                      {combo.colours.length > 5 && (
                        <span className="ml-1 text-[10px] font-medium text-stone-500">
                          +{combo.colours.length - 5}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <>
                <h4 className="font-medium text-stone-800 mb-2">
                  {combo.color_description || 'Custom Combination'}
                </h4>
                <p className="text-sm text-stone-600">
                  Finish: {combo.finish_type}
                </p>
              </>
            )}
          </div>
        ))}
      </div>

      {showAddToOrderButton && selectedCombo && (
        <div className="mt-8">
          {/* Preview and Order functionality */}
          {selectedProduct ? (
            <>
              {/* Add to Order Button */}
              <button
                type="button"
                disabled={addToOrderDisabled}
                onClick={() => {
                  if (addToOrderDisabled || !selectedProduct) return;

                  // Find the full vase data to get the image
                  const fullVaseData = vases.find(v => v.id === selectedProduct.id);

                  const newItem: OrderItem = {
                    vaseId: selectedProduct.id,
                    vaseName: selectedProduct.name,
                    vaseImage: fullVaseData?.image || '',
                    colorDescription: selectedCombo.color_description || 'Custom Combination',
                    finishType: selectedCombo.finish_type as ColourFinish,
                    colours: selectedCombo.colours || [],
                    quantity: 1, // Default quantity
                    // Merchant-only information
                    brandName: selectedCombo.brand,
                    colorName: selectedCombo.name,
                    fid: selectedCombo.fid,
                    blendSettings: undefined,
                    calibrationSettings: undefined,
                  };
                  onAddToOrder(newItem);
                }}
                className={`mt-4 w-full rounded-md px-6 py-3 text-lg font-medium transition-all ${
                  addToOrderDisabled
                    ? 'cursor-not-allowed border border-stone-300 bg-stone-200 text-stone-500'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {resolvedAddToOrderLabel}
              </button>
              {addToOrderDisabled ? (
                <p className="mt-2 text-center text-xs text-stone-500">
                  Visible to visitors. Switch to Interact to test the action.
                </p>
              ) : null}
            </>
          ) : (
            <p className="text-center text-stone-600 italic">
              Please select a vase from the collection above to see the live preview and add this combination
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ColourCombinations;
